import React from 'react'

import moment from 'moment'

import iconDocumentDoc from 'app/assets/icons/icon-document-doc.svg'
import iconDocumentImg from 'app/assets/icons/icon-document-img.svg'
import iconDocumentPdf from 'app/assets/icons/icon-document-pdf.svg'
import {
  DropElem,
  FilesDropzone,
} from 'app/components/design-system-components'
import * as Display from 'app/components/display/display'
import * as Text from 'app/components/display/text/text_component'
import * as Form from 'app/components/forms/forms'
import { setFileIcon, trimFileLength } from 'app/sm/helpers'
import { DATE_WITH_DASH } from 'app/utils/datetime/helpers'

const YES = 'Yes'
const NO = 'No'

//versions are only used in displaying disclosures, in forms (adding disclosures) we're always using latest version

const MISSING_VERSION = 0
const BASE_VERSION = 1
const SECOND_VERSION = 2
const THIRD_VERSION = 3
const ALL_VERSIONS = [
  MISSING_VERSION,
  BASE_VERSION,
  SECOND_VERSION,
  THIRD_VERSION,
]

export const getDisclosureLatestVersion = () => THIRD_VERSION
export const getDisclosureMissingVersion = () => MISSING_VERSION

export const disclosureVersionedQuestions = {
  hasHomicide: {
    versions: ALL_VERSIONS,
    getTextForVersion: (version) => {
      if (version === THIRD_VERSION) {
        return 'To the best of my knowledge, the property or common property has been the location of a homicide in the last 5 years'
      }
      return 'Was there a homicide at the property?'
    },
    text: '', // Set dynamically via getTextForVersion
    render: (value, err, isEditable, onChange, version) => (
      <DisclosureQuestion id={'hasHomicide'} version={version}>
        {renderDisclosureBooleanQuestion(
          value,
          err,
          isEditable,
          'hasHomicide',
          onChange,
        )}
      </DisclosureQuestion>
    ),
  },
  dateOfHomicide: {
    versions: [MISSING_VERSION, BASE_VERSION, SECOND_VERSION], // Excluded from THIRD_VERSION
    text: 'Date of Homicide',
    conditionalOn: 'hasHomicide',
    render: (value, err, isEditable, onChange, version) => (
      <DisclosureQuestion id={'dateOfHomicide'} version={version}>
        {renderDisclosurePastDateQuestion(
          value,
          err,
          isEditable,
          'dateOfHomicide',
          onChange,
        )}
      </DisclosureQuestion>
    ),
  },
  compliesWithMinimumStandards: {
    versions: ALL_VERSIONS,
    text: 'The property meets the rental minimum standards',
    render: (value, err, isEditable, onChange, version) => (
      <DisclosureQuestion id={'compliesWithMinimumStandards'} version={version}>
        {renderDisclosureBooleanQuestion(
          value,
          err,
          isEditable,
          'compliesWithMinimumStandards',
          onChange,
        )}
      </DisclosureQuestion>
    ),
  },
  dateOfRepairNotice: {
    versions: [MISSING_VERSION, BASE_VERSION, SECOND_VERSION], // V0-V2 only (replaced by boolean in V3)
    text: 'Date of most recent Repair Notice relating to Mould or Damp?',
    render: (value, err, isEditable, onChange, version) => (
      <DisclosureQuestion id={'dateOfRepairNotice'} version={version}>
        {renderDisclosurePastDateQuestion(
          value,
          err,
          isEditable,
          'dateOfRepairNotice',
          onChange,
        )}
      </DisclosureQuestion>
    ),
  },
  dateOfGasCheck: {
    versions: ALL_VERSIONS,
    text: 'When a gas safety check was last conducted at the premises',
    render: (value, err, isEditable, onChange, version) => (
      <DisclosureQuestion id={'dateOfGasCheck'} version={version}>
        {renderDisclosurePastDateQuestion(
          value,
          err,
          isEditable,
          'dateOfGasCheck',
          onChange,
        )}
      </DisclosureQuestion>
    ),
  },
  dateOfElectricityCheck: {
    versions: ALL_VERSIONS,
    text: 'When an electricity safety check was last conducted at the premises',
    render: (value, err, isEditable, onChange, version) => (
      <DisclosureQuestion id={'dateOfElectricityCheck'} version={version}>
        {renderDisclosurePastDateQuestion(
          value,
          err,
          isEditable,
          'dateOfElectricityCheck',
          onChange,
        )}
      </DisclosureQuestion>
    ),
  },
  dateOfPoolCheck: {
    versions: ALL_VERSIONS,
    text: 'When a pool barrier compliance check was last conducted at the premises (if applicable)',
    render: (value, err, isEditable, onChange, version) => (
      <DisclosureQuestion id={'dateOfPoolCheck'} version={version}>
        {renderDisclosurePastDateQuestion(
          value,
          err,
          isEditable,
          'dateOfPoolCheck',
          onChange,
        )}
      </DisclosureQuestion>
    ),
  },
  isRegisteredPlace: {
    versions: [MISSING_VERSION, BASE_VERSION, SECOND_VERSION], // Excluded from THIRD_VERSION
    text: 'Is the rented premises a Registered Place?',
    render: (value, err, isEditable, onChange, version) => (
      <DisclosureQuestion id={'isRegisteredPlace'} version={version}>
        {renderDisclosureBooleanQuestion(
          value,
          err,
          isEditable,
          'isRegisteredPlace',
          onChange,
        )}
      </DisclosureQuestion>
    ),
  },
  dateOfDrugUsage: {
    versions: [MISSING_VERSION, BASE_VERSION, SECOND_VERSION], // V0-V2 only (replaced by boolean in V3)
    text: 'Date premise last used as a if contaminated because of drug of dependence trafficking or cultivation?',
    render: (value, err, isEditable, onChange, version) => (
      <DisclosureQuestion id={'dateOfDrugUsage'} version={version}>
        {renderDisclosurePastDateQuestion(
          value,
          err,
          isEditable,
          'dateOfDrugUsage',
          onChange,
        )}
      </DisclosureQuestion>
    ),
  },
  hasAsbestos: {
    versions: ALL_VERSIONS,
    text: 'To the best of my knowledge, the property has asbestos (friable or non-friable) based on an inspection by a suitably qualified person',
    render: (value, err, isEditable, onChange, version) => (
      <DisclosureQuestion id={'hasAsbestos'} version={version}>
        {renderDisclosureBooleanQuestion(
          value,
          err,
          isEditable,
          'hasAsbestos',
          onChange,
        )}
      </DisclosureQuestion>
    ),
  },
  isAffectedWithRelevantAuthorities: {
    versions: ALL_VERSIONS,
    text: 'To the best of my knowledge, the property is affected by a building or planning application lodged with a relevant authority',
    render: (value, err, isEditable, onChange, version) => (
      <DisclosureQuestion
        id={'isAffectedWithRelevantAuthorities'}
        version={version}
      >
        {renderDisclosureBooleanQuestion(
          value,
          err,
          isEditable,
          'isAffectedWithRelevantAuthorities',
          onChange,
        )}
      </DisclosureQuestion>
    ),
  },
  hasEmbeddedNetwork: {
    versions: ALL_VERSIONS,
    text: 'The premises are supplied with electricity from an embedded electricity network',
    render: (value, err, isEditable, onChange, version) => (
      <DisclosureQuestion id={'hasEmbeddedNetwork'} version={version}>
        {renderDisclosureBooleanQuestion(
          value,
          err,
          isEditable,
          'hasEmbeddedNetwork',
          onChange,
        )}
      </DisclosureQuestion>
    ),
  },
  embeddedNetworkName: {
    versions: ALL_VERSIONS,
    text: 'Trading name of the embedded network operator',
    conditionalOn: 'hasEmbeddedNetwork',
    render: (value, err, isEditable, label, onChange, version) => (
      <DisclosureQuestion id={'embeddedNetworkName'} version={version}>
        {renderDisclosureOptionalTextQuestion(
          value,
          err,
          isEditable,
          'embeddedNetworkName',
          label,
          onChange,
        )}
      </DisclosureQuestion>
    ),
  },
  embeddedNetworkAbn: {
    versions: ALL_VERSIONS,
    text: 'ABN of the embedded network operator',
    conditionalOn: 'hasEmbeddedNetwork',
    render: (value, err, isEditable, label, onChange, version) => (
      <DisclosureQuestion id="embeddedNetworkAbn" version={version}>
        {renderDisclosureABNQuestion(
          version,
          value,
          err,
          isEditable,
          'embeddedNetworkAbn',
          label,
          onChange,
        )}
      </DisclosureQuestion>
    ),
  },
  embeddedNetworkPhone: {
    versions: ALL_VERSIONS,
    text: 'Phone number of the embedded network operator',
    conditionalOn: 'hasEmbeddedNetwork',
    render: (value, err, isEditable, label, onChange, version) => (
      <DisclosureQuestion id="embeddedNetworkPhone" version={version}>
        {renderDisclosurePhoneQuestion(
          value,
          err,
          isEditable,
          'embeddedNetworkPhone',
          label,
          onChange,
        )}
      </DisclosureQuestion>
    ),
  },
  embeddedNetworkWebsite: {
    versions: ALL_VERSIONS,
    text: 'Website address of the embedded network operator',
    conditionalOn: 'hasEmbeddedNetwork',
    render: (value, err, isEditable, label, onChange, version) => (
      <DisclosureQuestion id={'embeddedNetworkWebsite'} version={version}>
        {renderDisclosureOptionalTextQuestion(
          value,
          err,
          isEditable,
          'embeddedNetworkWebsite',
          label,
          onChange,
        )}
      </DisclosureQuestion>
    ),
  },
  embeddedNetworkPricing: {
    versions: ALL_VERSIONS,
    text: 'Electricity tariffs and all other fees and charges applicable (or where that information may be accessed)',
    conditionalOn: 'hasEmbeddedNetwork',
    render: (value, err, isEditable, label, onChange, version) => (
      <DisclosureQuestion id={'embeddedNetworkPricing'} version={version}>
        {renderDisclosureOptionalTextQuestion(
          value,
          err,
          isEditable,
          'embeddedNetworkPricing',
          label,
          onChange,
        )}
      </DisclosureQuestion>
    ),
  },
  isBeingSold: {
    versions: [SECOND_VERSION, THIRD_VERSION],
    getTextForVersion: (version) => {
      if (version === THIRD_VERSION) {
        return 'I intend to sell the premises'
      }
      return 'Has an agent/person been engaged to sell the property or, if a contract of sale has been prepared, is there an ongoing proposal to sell the property?'
    },
    text: '', // Set dynamically via getTextForVersion
    render: (value, err, isEditable, onChange, version) => (
      <DisclosureQuestion id={'isBeingSold'} version={version}>
        {renderDisclosureBooleanQuestion(
          value,
          err,
          isEditable,
          'isBeingSold',
          onChange,
        )}
      </DisclosureQuestion>
    ),
  },

  // ========================================================================
  // VERSION 3 NEW QUESTIONS
  // ========================================================================

  // Q1a: Property Sale Sub-question
  has_engaged_agent_to_sell: {
    versions: [THIRD_VERSION],
    text: 'I have engaged an agent to sell the property',
    conditionalOn: 'isBeingSold',
    render: (value, err, isEditable, onChange, version) => (
      <DisclosureQuestion id={'has_engaged_agent_to_sell'} version={version}>
        {renderDisclosureBooleanQuestion(
          value,
          err,
          isEditable,
          'has_engaged_agent_to_sell',
          onChange,
        )}
      </DisclosureQuestion>
    ),
  },

  // Q1b: Property Sale Sub-question
  has_contract_of_sale_prepared: {
    versions: [THIRD_VERSION],
    text: 'A contract of sale has been prepared for the property',
    conditionalOn: 'isBeingSold',
    render: (value, err, isEditable, onChange, version) => (
      <DisclosureQuestion
        id={'has_contract_of_sale_prepared'}
        version={version}
      >
        {renderDisclosureBooleanQuestion(
          value,
          err,
          isEditable,
          'has_contract_of_sale_prepared',
          onChange,
        )}
      </DisclosureQuestion>
    ),
  },

  // Q2: Mortgagee Action
  has_mortgagee_action: {
    versions: [THIRD_VERSION],
    text: 'A mortgagee is taking action to possess the premises',
    render: (value, err, isEditable, onChange, version) => (
      <DisclosureQuestion id={'has_mortgagee_action'} version={version}>
        {renderDisclosureBooleanQuestion(
          value,
          err,
          isEditable,
          'has_mortgagee_action',
          onChange,
        )}
      </DisclosureQuestion>
    ),
  },

  // Q2a: Mortgagee Sub-question
  mortgagee_has_commenced_proceedings: {
    versions: [THIRD_VERSION],
    text: 'A mortgagee has commenced proceedings to enforce the mortgage',
    conditionalOn: 'has_mortgagee_action',
    render: (value, err, isEditable, onChange, version) => (
      <DisclosureQuestion
        id={'mortgagee_has_commenced_proceedings'}
        version={version}
      >
        {renderDisclosureBooleanQuestion(
          value,
          err,
          isEditable,
          'mortgagee_has_commenced_proceedings',
          onChange,
        )}
      </DisclosureQuestion>
    ),
  },

  // Q3: Property Ownership
  is_property_owner: {
    versions: [THIRD_VERSION],
    text: 'I am the owner of the property',
    render: (value, err, isEditable, onChange, version) => (
      <DisclosureQuestion id={'is_property_owner'} version={version}>
        {renderDisclosureBooleanQuestion(
          value,
          err,
          isEditable,
          'is_property_owner',
          onChange,
        )}
      </DisclosureQuestion>
    ),
  },

  // Q3a: Property Ownership Sub-question (shown when is_property_owner = No)
  has_right_to_rent: {
    versions: [THIRD_VERSION],
    text: 'I am not the owner of the property but I have a right to rent the property out',
    conditionalOn: 'is_property_owner',
    conditionalOnValue: false,
    render: (value, err, isEditable, onChange, version) => (
      <DisclosureQuestion id={'has_right_to_rent'} version={version}>
        {renderDisclosureBooleanQuestion(
          value,
          err,
          isEditable,
          'has_right_to_rent',
          onChange,
        )}
      </DisclosureQuestion>
    ),
  },

  // Q7: Mould/Damp - V3 Boolean version (3-year timeframe)
  has_mould_damp_repair_notice_3yr: {
    versions: [THIRD_VERSION],
    text: 'In the last 3 years, I have received a repair notice(s) seeking repair of mould or damp caused by or related to the building structure',
    render: (value, err, isEditable, onChange, version) => (
      <DisclosureQuestion
        id={'has_mould_damp_repair_notice_3yr'}
        version={version}
      >
        {renderDisclosureBooleanQuestion(
          value,
          err,
          isEditable,
          'has_mould_damp_repair_notice_3yr',
          onChange,
        )}
      </DisclosureQuestion>
    ),
  },

  // Q11: Outstanding Safety Recommendations
  has_outstanding_safety_recommendations: {
    versions: [THIRD_VERSION],
    text: "Whether there are any outstanding recommendations from the electricity or gas check. If the answer is yes, a description of the outstanding recommendations is to be provided in 'Further information' below",
    render: (value, err, isEditable, onChange, version) => (
      <DisclosureQuestion
        id={'has_outstanding_safety_recommendations'}
        version={version}
      >
        {renderDisclosureBooleanQuestion(
          value,
          err,
          isEditable,
          'has_outstanding_safety_recommendations',
          onChange,
        )}
      </DisclosureQuestion>
    ),
  },

  // Q12: Heritage Listed
  is_heritage_listed: {
    versions: [THIRD_VERSION],
    text: 'The property is a heritage listed place on the Heritage Register',
    render: (value, err, isEditable, onChange, version) => (
      <DisclosureQuestion id={'is_heritage_listed'} version={version}>
        {renderDisclosureBooleanQuestion(
          value,
          err,
          isEditable,
          'is_heritage_listed',
          onChange,
        )}
      </DisclosureQuestion>
    ),
  },

  // Q13: Drug Usage - V3 Boolean version (5-year timeframe)
  has_drug_usage_5yr: {
    versions: [THIRD_VERSION],
    text: 'To the best of my knowledge, the property has been used for the trafficking or cultivation of a drug of dependence in the last 5 years',
    render: (value, err, isEditable, onChange, version) => (
      <DisclosureQuestion id={'has_drug_usage_5yr'} version={version}>
        {renderDisclosureBooleanQuestion(
          value,
          err,
          isEditable,
          'has_drug_usage_5yr',
          onChange,
        )}
      </DisclosureQuestion>
    ),
  },

  // Q16: Building Defects/Safety Concerns
  has_defect_safety_concern_notice: {
    versions: [THIRD_VERSION],
    text: "The rental premises are the subject of a notice, order, declaration, report or recommendation issued by a relevant building surveyor, municipal building surveyor, public authority or government department relating to any building defects or safety concerns associated with the rented premises or common property at the time of disclosure. If the answer is yes, a description of the notice, order, declaration, report or recommendation is to be provided in 'Further information' below",
    render: (value, err, isEditable, onChange, version) => (
      <DisclosureQuestion
        id={'has_defect_safety_concern_notice'}
        version={version}
      >
        {renderDisclosureBooleanQuestion(
          value,
          err,
          isEditable,
          'has_defect_safety_concern_notice',
          onChange,
        )}
      </DisclosureQuestion>
    ),
  },

  // Q17: Domestic Building Dispute
  has_domestic_building_dispute: {
    versions: [THIRD_VERSION],
    text: 'There is a current domestic building work dispute under the Domestic Building Contracts Act 1995 relating to the premises',
    render: (value, err, isEditable, onChange, version) => (
      <DisclosureQuestion
        id={'has_domestic_building_dispute'}
        version={version}
      >
        {renderDisclosureBooleanQuestion(
          value,
          err,
          isEditable,
          'has_domestic_building_dispute',
          onChange,
        )}
      </DisclosureQuestion>
    ),
  },

  // Q18: Owners Corporation Dispute
  has_owners_corporation_dispute: {
    versions: [THIRD_VERSION],
    text: 'There is a current dispute under Part 10 of the Owners Corporations Act 2006 which applies to or affects the premises',
    render: (value, err, isEditable, onChange, version) => (
      <DisclosureQuestion
        id={'has_owners_corporation_dispute'}
        version={version}
      >
        {renderDisclosureBooleanQuestion(
          value,
          err,
          isEditable,
          'has_owners_corporation_dispute',
          onChange,
        )}
      </DisclosureQuestion>
    ),
  },

  // Q19: Owners Corporation Rules
  has_owners_corporation_rules: {
    versions: [THIRD_VERSION],
    text: 'Whether the premises is subject to any owners corporation rules (rental provider to attach the rules to this application form)',
    render: (value, err, isEditable, onChange, version) => (
      <DisclosureQuestion id={'has_owners_corporation_rules'} version={version}>
        {renderDisclosureBooleanQuestion(
          value,
          err,
          isEditable,
          'has_owners_corporation_rules',
          onChange,
        )}
      </DisclosureQuestion>
    ),
  },

  // Q19a: Owners Corporation Rules Attachment
  owners_corporation_rules_attachment: {
    versions: [THIRD_VERSION],
    text: 'Attach owners corporation rules document',
    conditionalOn: 'has_owners_corporation_rules',
    render: (
      value,
      err,
      isEditable,
      id,
      onFileUpload,
      uploading,
      uploadError,
      onFileDelete,
    ) => (
      <DisclosureQuestion id={id} version={THIRD_VERSION}>
        {renderDisclosureFileUploadQuestion(
          value,
          err,
          isEditable,
          id,
          onFileUpload,
          uploading,
          uploadError,
          onFileDelete,
        )}
      </DisclosureQuestion>
    ),
  },

  // General notes field for all questions (always visible)
  further_information: {
    versions: [THIRD_VERSION],
    text: 'Further information about any disclosures outlined above',
    render: (value, err, isEditable, onChange, version) => {
      // Don't render label since we already have the heading from DisclosureQuestion
      return (
        <DisclosureQuestion id={'further_information'} version={version}>
          {renderDisclosureOptionalTextAreaQuestion(
            value,
            err,
            isEditable,
            'further_information',
            '', // Empty label to avoid duplication with heading
            onChange,
          )}
        </DisclosureQuestion>
      )
    },
  },

  // ========================================================================
  // CAMELCASE ALIASES FOR V3 QUESTIONS (used by DisclosureQuestionsDisplay)
  // These reference the snake_case versions above for consistency
  // ========================================================================
  hasEngagedAgentToSell: {
    versions: [THIRD_VERSION],
    text: 'I have engaged an agent to sell the property',
  },
  hasContractOfSalePrepared: {
    versions: [THIRD_VERSION],
    text: 'A contract of sale has been prepared for the property',
  },
  hasMortgageeAction: {
    versions: [THIRD_VERSION],
    text: 'A mortgagee is taking action to possess the premises',
  },
  mortgageeHasCommencedProceedings: {
    versions: [THIRD_VERSION],
    text: 'A mortgagee has commenced proceedings to enforce the mortgage',
  },
  isPropertyOwner: {
    versions: [THIRD_VERSION],
    text: 'I am the owner of the property',
  },
  hasMouldDampRepairNotice3Yr: {
    versions: [THIRD_VERSION],
    text: 'In the last 3 years, I have received a repair notice(s) seeking repair of mould or damp caused by or related to the building structure',
  },
  hasOutstandingSafetyRecommendations: {
    versions: [THIRD_VERSION],
    text: "Whether there are any outstanding recommendations from the electricity or gas check. If the answer is yes, a description of the outstanding recommendations is to be provided in 'Further information' below",
  },
  isHeritageListed: {
    versions: [THIRD_VERSION],
    text: 'The property is a heritage listed place on the Heritage Register',
  },
  hasDrugUsage5Yr: {
    versions: [THIRD_VERSION],
    text: 'To the best of my knowledge, the property has been used for the trafficking or cultivation of a drug of dependence in the last 5 years',
  },
  hasDefectSafetyConcernNotice: {
    versions: [THIRD_VERSION],
    text: "The rental premises are the subject of a notice, order, declaration, report or recommendation issued by a relevant building surveyor, municipal building surveyor, public authority or government department relating to any building defects or safety concerns associated with the rented premises or common property at the time of disclosure. If the answer is yes, a description of the notice, order, declaration, report or recommendation is to be provided in 'Further information' below",
  },
  hasDomesticBuildingDispute: {
    versions: [THIRD_VERSION],
    text: 'There is a current domestic building work dispute under the Domestic Building Contracts Act 1995 relating to the premises',
  },
  hasOwnersCorporationDispute: {
    versions: [THIRD_VERSION],
    text: 'There is a current dispute under Part 10 of the Owners Corporations Act 2006 which applies to or affects the premises',
  },
  hasOwnersCorporationRules: {
    versions: [THIRD_VERSION],
    text: 'Whether the premises is subject to any owners corporation rules (rental provider to attach the rules to this application form)',
  },
}

const renderDisclosureBooleanQuestion = (
  value,
  err,
  isEditable,
  id,
  onChange,
) => {
  return (
    <>
      <Form.YesOrNoRadioGroup id={id} error={err}>
        <Form.YesOrNoCheckbox
          id={YES}
          label={YES}
          checked={value === true}
          onChange={onChange(id)}
          componentClassName="yes left"
          enableEdit={isEditable}
        />
        <Form.YesOrNoCheckbox
          id={NO}
          label={NO}
          checked={value === false}
          onChange={onChange(id)}
          componentClassName="no right"
          enableEdit={isEditable}
        />
      </Form.YesOrNoRadioGroup>
    </>
  )
}

const renderDisclosurePastDateQuestion = (
  value,
  err,
  isEditable,
  id,
  onChange,
) => (
  <>
    <Form.InputPastDate
      value={moment(value)}
      onChange={onChange(id)}
      id={id}
      readOnly={!isEditable}
      error={err}
      customisedDateFormat={DATE_WITH_DASH}
    />
  </>
)
const renderDisclosureOptionalTextQuestion = (
  value,
  err,
  isEditable,
  id,
  label,
  onChange,
) => (
  <>
    <Form.InputTextOptional
      value={value}
      label={isEditable ? label : ''}
      onChange={onChange(id)}
      labelClass="top18"
      inputClass="width100"
      isOptional
      readOnly={!isEditable}
      showError={true}
      error={err}
    />
  </>
)

const renderDisclosureOptionalTextAreaQuestion = (
  value,
  err,
  isEditable,
  id,
  label,
  onChange,
) => (
  <>
    <Form.CommentArea
      value={value}
      label={isEditable ? label : ''}
      onChange={onChange(id)}
      labelClass="top18"
      inputClass="width100"
      isOptional
      readOnly={!isEditable}
      showError={true}
      error={err}
      rows={5}
    />
  </>
)

const renderDisclosureABNQuestion = (
  version,
  value,
  err,
  isEditable,
  id,
  label,
  onChange,
) => (
  <>
    <Form.InputABN
      value={value}
      label={isEditable ? label : ''}
      onChange={onChange(id)}
      labelClass="top18"
      inputClass="width100"
      isOptional
      readOnly={!isEditable}
      error={err}
    />
  </>
)

const renderDisclosurePhoneQuestion = (
  value,
  err,
  isEditable,
  id,
  label,
  onChange,
) => (
  <>
    <Form.InputPhoneNumber
      value={value}
      label={isEditable ? label : ''}
      onChange={onChange(id)}
      labelClass="top18"
      inputClass="width100"
      isOptional
      readOnly={!isEditable}
      error={err}
    />
  </>
)

// Helper to get file extension from name
const getFileExtension = (filename) =>
  filename && filename.substring(filename.lastIndexOf('.') + 1, filename.length)

// Document icons mapping
const documentIcons = {
  doc: iconDocumentDoc,
  img: iconDocumentImg,
  pdf: iconDocumentPdf,
}

// Extract file metadata from value object
const getFileMetadata = (value) => {
  const filename = value.name || 'Document attached'
  const fileExtension = getFileExtension(filename) || 'pdf'
  const fileIconName = setFileIcon(fileExtension)
  const displayFilename = trimFileLength(11)(filename)
  const date = value.createdAt
    ? moment(value.createdAt).format('DD MMM YYYY')
    : moment().format('DD MMM YYYY')

  return { filename, fileIconName, displayFilename, date }
}

// Render file link or plain text
const renderFileLink = (value, displayFilename, filename) => {
  if (value.url) {
    return (
      <a
        className="gray-dark-color"
        target="_blank"
        href={value.url}
        download={filename}
        rel="noreferrer"
      >
        {displayFilename}
      </a>
    )
  }
  return <>{displayFilename}</>
}

// Render error and uploading status messages
const renderStatusMessages = (err, uploadError, uploading) => (
  <>
    {(err || uploadError) && (
      <div className="error-message" style={{ color: 'red', marginTop: '5px' }}>
        {err || uploadError}
      </div>
    )}
    {uploading && (
      <div style={{ color: '#666', marginTop: '5px', fontSize: '14px' }}>
        Uploading file, please wait...
      </div>
    )}
  </>
)

// Render file table (used for both display and edit modes with existing file)
const renderFileTable = (
  value,
  fileIconName,
  displayFilename,
  filename,
  date,
  showDelete,
  onFileDelete,
  id,
) => (
  <table className="style3">
    <thead>
      <tr>
        <td colSpan="2">Name</td>
        <td>Date</td>
        {showDelete && <td />}
      </tr>
    </thead>
    <tbody>
      <tr>
        <td className="pr0 w1p">
          <img alt="" src={documentIcons[fileIconName]} height="25" />
        </td>
        <td>{renderFileLink(value, displayFilename, filename)}</td>
        <td>{date}</td>
        {showDelete && onFileDelete && (
          <td className="actionButtonWrapper">
            <button
              type="button"
              onClick={() => onFileDelete(id)}
              className="remove-btn"
            >
              <i className="icon-remove" aria-hidden="true" />
            </button>
          </td>
        )}
      </tr>
    </tbody>
  </table>
)

const renderDisclosureFileUploadQuestion = (
  value,
  err,
  isEditable,
  id,
  onFileUpload,
  uploading,
  uploadError,
  onFileDelete,
) => {
  // Display mode with file - show read-only file info
  if (!isEditable && value) {
    const { filename, fileIconName, displayFilename, date } =
      getFileMetadata(value)
    return renderFileTable(
      value,
      fileIconName,
      displayFilename,
      filename,
      date,
      false,
      null,
      id,
    )
  }

  // Display mode without file
  if (!isEditable && !value) {
    return (
      <Display.FlexContainer>
        <Text.Normal text="No document attached" />
      </Display.FlexContainer>
    )
  }

  // Edit mode with existing file - show file info with delete option
  if (isEditable && value) {
    const { filename, fileIconName, displayFilename, date } =
      getFileMetadata(value)
    return (
      <>
        {renderFileTable(
          value,
          fileIconName,
          displayFilename,
          filename,
          date,
          true,
          onFileDelete,
          id,
        )}
        {renderStatusMessages(err, uploadError, uploading)}
      </>
    )
  }

  // Edit mode without file - show upload widget
  return (
    <>
      <FilesDropzone
        onDrop={(acceptedFiles, rejectedFiles) => {
          if (rejectedFiles && rejectedFiles.length > 0) {
            // File was rejected by dropzone (wrong type)
            // Pass the rejected file to trigger validation error message
            const rejectedFile = rejectedFiles[0].file || rejectedFiles[0]
            onFileUpload(id, rejectedFile)
          } else if (acceptedFiles && acceptedFiles.length > 0) {
            onFileUpload(id, acceptedFiles[0])
          }
        }}
        accept=".pdf"
        multiple={false}
        dropElementRenderer={() => (
          <DropElem
            mainActionText={
              <span style={{ color: '#374151' }}>
                {uploading ? 'Uploading...' : 'Click to upload'}
              </span>
            }
            subElem={<span>(PDF only, max 10MB)</span>}
          />
        )}
      />
      {renderStatusMessages(err, uploadError, uploading)}
    </>
  )
}

const DisclosureQuestion = ({ id, version, children }) => {
  const question = disclosureVersionedQuestions[id]
  if (!question?.versions?.includes(version)) {
    return null
  }

  // Get version-specific text if available, otherwise use static text
  const questionText = question.getTextForVersion
    ? question.getTextForVersion(version)
    : question.text

  return (
    <>
      {questionText && (
        <Display.FlexContainer>
          <Text.Strong text={questionText} />
        </Display.FlexContainer>
      )}
      {question.helperText && (
        <Display.FlexContainer>
          <Text.Normal
            text={question.helperText}
            componentClass="font-size-14"
          />
        </Display.FlexContainer>
      )}
      {children}
    </>
  )
}
